import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Rocket, RefreshCcw, ShieldCheck, FileDown } from "lucide-react";

export default function EventSettings() {
  const [s, setS] = useState(null);
  const [mode, setMode] = useState(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [wipeVotes, setWipeVotes] = useState(true);
  const [resetOpen, setResetOpen] = useState(false);
  const [votesOpen, setVotesOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [guestClosed, setGuestClosed] = useState(null);

  const loadMode = () => api.get("/project/mode").then((r) => setMode(r.data));
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    api.get("/guest-portal").then((r) => setGuestClosed(r.data.closed));
    loadMode();
  }, []);

  const toggleGuest = async () => {
    try {
      const { data } = await api.put("/guest-portal", { closed: !guestClosed });
      setGuestClosed(data.closed);
      toast.success(data.closed ? "Guest pages closed" : "Guest pages reopened");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!s) return <div className="text-slate-400">Loading…</div>;

  const save = async () => {
    try {
      await api.put("/settings", {
        event_name: s.event_name, event_date: s.event_date || null, event_location: s.event_location || null,
        default_regular_drinks: Number(s.default_regular_drinks), default_vip_drinks: Number(s.default_vip_drinks),
        barcode_format: s.barcode_format || "PDF417",
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetSales = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/project/reset-sales");
      toast.success(`Reset · purchases:${data.reset.purchases_deleted}, redemptions:${data.reset.redemptions_deleted}, tickets:${data.reset.tickets_reset}`);
      setResetOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const resetVotes = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/project/reset-votes");
      toast.success(`Votes reset · ${data.reset.votes_deleted} deleted`);
      setVotesOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const promote = async () => {
    if (confirm !== "PROMOTE") { toast.error('Type "PROMOTE" to confirm'); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/project/promote", { confirm, wipe_votes: wipeVotes });
      toast.success(`Promoted to PRODUCTION · tickets reset: ${data.reset.tickets_reset}`);
      setPromoteOpen(false); setConfirm(""); loadMode();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const revert = async () => {
    if (!window.confirm("Revert back to DEVELOP mode? Nothing is wiped.")) return;
    try { await api.post("/project/revert-to-develop"); toast.success("Back to DEVELOP"); loadMode(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const isProd = mode?.mode === "PRODUCTION";

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Configure</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">EVENT SETTINGS</h1>
      </div>

      <div className="rounded-2xl p-5 bg-[#131D33] border border-slate-800 flex items-center justify-between gap-3 flex-wrap" data-testid="user-guide-card">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-400 font-bold">Staff Guide</div>
          <div className="text-white font-bold">Ticket &amp; Voting System User Guide</div>
          <div className="text-xs text-slate-400">Printable PDF · US Letter · 9 pages</div>
        </div>
        <a href={`${process.env.PUBLIC_URL || ""}/docs/Food_Bazaar_Ticket_and_Voting_System_User_Guide.pdf`}
          download="Food Bazaar — Ticket & Voting System User Guide.pdf"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-colors"
          data-testid="download-user-guide-btn">
          <FileDown size={16} /> Download PDF
        </a>
      </div>

      <div className={`rounded-2xl p-5 border flex items-center justify-between gap-3 flex-wrap ${guestClosed ? "bg-rose-500/10 border-rose-500/40" : "bg-emerald-500/10 border-emerald-500/40"}`} data-testid="guest-portal-card">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-400 font-bold">Guest Pages (/event, /vote, /recharge)</div>
          <div className={`font-display text-2xl font-black ${guestClosed ? "text-rose-300" : "text-emerald-300"}`} data-testid="guest-portal-status">{guestClosed ? "CLOSED — Event ended" : "OPEN"}</div>
          <div className="text-xs text-slate-400">Closed: guests see a thank-you / see you next October page. Staff login is not affected.</div>
        </div>
        <Button onClick={toggleGuest} disabled={guestClosed === null} className={guestClosed ? "bg-emerald-600 hover:bg-emerald-700 text-white font-bold" : "bg-rose-600 hover:bg-rose-700 text-white font-bold"} data-testid="guest-portal-toggle">
          {guestClosed ? "Reopen Guest Pages" : "Close Guest Pages"}
        </Button>
      </div>

      {/* Project Mode */}
      <div className={`rounded-2xl p-5 border ${isProd ? "bg-emerald-500/10 border-emerald-500/40" : "bg-amber-500/10 border-amber-500/40"}`} data-testid="project-mode-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-slate-400 font-bold">Project Mode</div>
            <div className={`font-display text-3xl font-black tracking-tight ${isProd ? "text-emerald-300" : "text-amber-300"}`} data-testid="project-mode-value">
              {mode?.mode || "DEVELOP"}
            </div>
            {isProd && mode?.promoted_at && (
              <div className="text-xs text-slate-400 mt-1">Live since {new Date(mode.promoted_at).toLocaleString()} · by {mode.promoted_by}</div>
            )}
            {!isProd && (
              <div className="text-xs text-slate-400 mt-1">Test freely. Flip to Production right before guests arrive — it wipes all testing data.</div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {!isProd && (
              <>
                <Button variant="outline" onClick={() => setResetOpen(true)} className="bg-slate-900 border-slate-700 text-slate-200" data-testid="reset-sales-btn">
                  <RefreshCcw size={14} className="mr-1" /> Reset Drink Sales
                </Button>
                <Button variant="outline" onClick={() => setVotesOpen(true)} className="bg-slate-900 border-slate-700 text-slate-200" data-testid="reset-votes-btn">
                  <RefreshCcw size={14} className="mr-1" /> Reset Votes
                </Button>
                <Button onClick={() => setPromoteOpen(true)} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold" data-testid="promote-btn">
                  <Rocket size={14} className="mr-1" /> Move to Production
                </Button>
              </>
            )}
            {isProd && (
              <Button variant="outline" onClick={revert} className="bg-slate-900 border-slate-700 text-slate-300" data-testid="revert-btn">
                <ShieldCheck size={14} className="mr-1" /> Back to Develop
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Event fields */}
      <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6 space-y-4">
        <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Event</div>
        <div>
          <Label className="text-xs tracking-widest uppercase text-slate-400">Event Name</Label>
          <Input value={s.event_name} onChange={(e) => setS({ ...s, event_name: e.target.value })} className="bg-[#0B1120] border-slate-700 text-white" data-testid="settings-event-name" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Event Date</Label>
            <Input type="date" value={s.event_date || ""} onChange={(e) => setS({ ...s, event_date: e.target.value })} className="bg-[#0B1120] border-slate-700 text-white" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Event Location</Label>
            <Input value={s.event_location || ""} onChange={(e) => setS({ ...s, event_location: e.target.value })} className="bg-[#0B1120] border-slate-700 text-white" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Default Regular Drinks</Label>
            <Input type="number" min="0" value={s.default_regular_drinks} onChange={(e) => setS({ ...s, default_regular_drinks: e.target.value })} className="bg-[#0B1120] border-slate-700 text-white" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Default VIP Drinks</Label>
            <Input type="number" min="0" value={s.default_vip_drinks} onChange={(e) => setS({ ...s, default_vip_drinks: e.target.value })} className="bg-[#0B1120] border-slate-700 text-white" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Barcode Format</Label>
            <Input value={s.barcode_format} onChange={(e) => setS({ ...s, barcode_format: e.target.value })} className="bg-[#0B1120] border-slate-700 text-white" />
          </div>
        </div>
        <Button onClick={save} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="settings-save-btn">Save</Button>
      </div>

      {/* Reset confirm */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-[#131D33] border-slate-700 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2"><AlertTriangle className="text-amber-400" /> RESET DRINK SALES</DialogTitle></DialogHeader>
          <div className="rounded-xl bg-[#0B1120] border border-slate-800 p-4 text-sm text-slate-300">
            Wipes: purchases, redemptions, pending Stripe payments, print jobs. Resets every ticket's drinks back to the base amount and its drink credit to $0. <b>Votes are kept.</b> Tickets, barcodes, roles, users and settings are kept.
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => setResetOpen(false)} className="flex-1 bg-slate-800 border-slate-700 text-slate-200">CANCEL</Button>
            <Button onClick={resetSales} disabled={busy} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold" data-testid="reset-sales-confirm">{busy ? "Resetting…" : "RESET NOW"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset votes confirm */}
      <Dialog open={votesOpen} onOpenChange={setVotesOpen}>
        <DialogContent className="bg-[#131D33] border-slate-700 text-white max-w-lg" data-testid="reset-votes-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2"><AlertTriangle className="text-amber-400" /> RESET VOTES</DialogTitle></DialogHeader>
          <div className="rounded-xl bg-[#0B1120] border border-slate-800 p-4 text-sm text-slate-300">
            Deletes <b>all votes</b> so every ticket can vote again in each category. Drinks, sales, tickets, guests and settings are kept. This cannot be undone.
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => setVotesOpen(false)} className="flex-1 bg-slate-800 border-slate-700 text-slate-200" data-testid="reset-votes-cancel">CANCEL</Button>
            <Button onClick={resetVotes} disabled={busy} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold" data-testid="reset-votes-confirm">{busy ? "Resetting…" : "RESET VOTES"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Promote confirm */}
      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent className="bg-[#131D33] border-slate-700 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2"><Rocket className="text-emerald-400" /> MOVE TO PRODUCTION</DialogTitle></DialogHeader>
          <div className="rounded-xl bg-[#0B1120] border border-rose-900/50 p-4 text-sm text-rose-200 space-y-2">
            <div className="font-bold text-rose-300">This is destructive. It cannot be undone.</div>
            <div>Wipes: drink purchases, redemptions, pending Stripe payments, print jobs{wipeVotes ? ", contest votes" : ""}. Resets every ticket back to its base drinks balance.</div>
            <div>Kept: tickets, barcodes, roles, users, event settings, templates.</div>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-300 mt-2">
            <input type="checkbox" checked={wipeVotes} onChange={(e) => setWipeVotes(e.target.checked)} data-testid="wipe-votes" />
            Also wipe contest votes
          </label>
          <div className="mt-3">
            <Label className="text-xs tracking-widest uppercase text-slate-400">Type <b>PROMOTE</b> to continue</Label>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-[#0B1120] border-slate-700 text-white font-mono-fb" data-testid="promote-confirm-input" />
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => { setPromoteOpen(false); setConfirm(""); }} className="flex-1 bg-slate-800 border-slate-700 text-slate-200">CANCEL</Button>
            <Button onClick={promote} disabled={busy || confirm !== "PROMOTE"} className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold" data-testid="promote-confirm">
              {busy ? "Promoting…" : "GO LIVE"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
